
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function OfflineFeaturesPage() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineOrders, setOfflineOrders] = useState<any[]>([]);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineData();
    };
    
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Charger les données hors ligne depuis localStorage
    loadOfflineData();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadOfflineData = () => {
    const stored = localStorage.getItem('onapila_offline_orders');
    if (stored) {
      setOfflineOrders(JSON.parse(stored));
    }
  };

  const syncOfflineData = async () => {
    if (offlineOrders.length === 0) return;
    
    setSyncStatus('syncing');
    
    try {
      // Simulation de synchronisation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Vider les données hors ligne après synchronisation
      localStorage.removeItem('onapila_offline_orders');
      setOfflineOrders([]);
      setSyncStatus('success');
      
      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch (error) {
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 3000);
    }
  };

  const createOfflineOrder = (orderData: any) => {
    const newOrder = {
      id: Date.now().toString(),
      ...orderData,
      createdAt: new Date().toISOString(),
      status: 'offline_pending'
    };
    
    const updatedOrders = [...offlineOrders, newOrder];
    setOfflineOrders(updatedOrders);
    localStorage.setItem('onapila_offline_orders', JSON.stringify(updatedOrders));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Pacifico, serif' }}>
                ONAPILA
              </Link>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600 font-medium">Mode Hors Ligne</span>
            </div>
            
            {/* Indicateur de connexion */}
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                isOnline 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-orange-100 text-orange-800'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  isOnline ? 'bg-green-500' : 'bg-orange-500'
                } ${!isOnline ? 'animate-pulse' : ''}`}></div>
                {isOnline ? 'En ligne' : 'Hors ligne'}
              </div>
              
              {syncStatus === 'syncing' && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <i className="ri-refresh-line animate-spin"></i>
                  <span className="text-sm">Synchronisation...</span>
                </div>
              )}
              
              {syncStatus === 'success' && (
                <div className="flex items-center space-x-2 text-green-600">
                  <i className="ri-check-line"></i>
                  <span className="text-sm">Synchronisé</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {!isOnline && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <i className="ri-wifi-off-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-orange-900">Mode Hors Ligne Activé</h3>
                <p className="text-orange-700">Vous pouvez continuer à utiliser ONAPILA. Vos données seront synchronisées dès la reconnexion.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Fonctionnalités Hors Ligne */}
          <div className="lg:col-span-2 space-y-8">
            {/* Créer une commande hors ligne */}
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer une commande hors ligne</h2>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                createOfflineOrder({
                  type: formData.get('type'),
                  from: formData.get('from'),
                  to: formData.get('to'),
                  description: formData.get('description'),
                  urgency: formData.get('urgency')
                });
                (e.target as HTMLFormElement).reset();
              }}>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type de colis</label>
                    <select name="type" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                      <option value="documents">Documents</option>
                      <option value="colis">Colis</option>
                      <option value="medicaments">Médicaments</option>
                      <option value="produits-frais">Produits frais</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Urgence</label>
                    <select name="urgency" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="express">Express</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Point de départ</label>
                    <input 
                      type="text" 
                      name="from"
                      placeholder="Adresse de départ"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                    <input 
                      type="text" 
                      name="to"
                      placeholder="Adresse de destination"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea 
                    name="description"
                    rows={3}
                    placeholder="Décrivez votre colis..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-save-line mr-2"></i>
                  Sauvegarder hors ligne
                </button>
              </form>
            </div>

            {/* Commandes en attente de synchronisation */}
            {offlineOrders.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Commandes en attente de synchronisation</h3>
                  <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                    {offlineOrders.length} en attente
                  </span>
                </div>

                <div className="space-y-4">
                  {offlineOrders.map((order) => (
                    <div key={order.id} className="p-4 border border-orange-200 rounded-xl bg-orange-50">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{order.type}</h4>
                          <p className="text-sm text-gray-600">{order.from} → {order.to}</p>
                          {order.description && (
                            <p className="text-sm text-gray-500 mt-1">{order.description}</p>
                          )}
                        </div>
                        <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                          {order.urgency}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Créé le {new Date(order.createdAt).toLocaleDateString()}</span>
                        <div className="flex items-center space-x-1">
                          <i className="ri-time-line text-orange-500"></i>
                          <span>En attente de synchronisation</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {isOnline && (
                  <button 
                    onClick={syncOfflineData}
                    disabled={syncStatus === 'syncing'}
                    className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {syncStatus === 'syncing' ? (
                      <>
                        <i className="ri-refresh-line animate-spin mr-2"></i>
                        Synchronisation en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-line mr-2"></i>
                        Synchroniser maintenant
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Fonctionnalités disponibles hors ligne */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Fonctionnalités hors ligne</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <i className="ri-check-line text-green-600"></i>
                  <span className="text-gray-700 text-sm">Création de commandes</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <i className="ri-check-line text-green-600"></i>
                  <span className="text-gray-700 text-sm">Consultation historique</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <i className="ri-check-line text-green-600"></i>
                  <span className="text-gray-700 text-sm">Gestion des adresses</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <i className="ri-check-line text-green-600"></i>
                  <span className="text-gray-700 text-sm">Calcul des tarifs</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-close-line text-gray-400"></i>
                  <span className="text-gray-500 text-sm">Suivi temps réel</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-close-line text-gray-400"></i>
                  <span className="text-gray-500 text-sm">Paiements en ligne</span>
                </div>
              </div>
            </div>

            {/* Optimisation des données */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Optimisation des données</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Données utilisées</span>
                  <span className="font-semibold text-gray-900">&lt; 50 Ko</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Compression</span>
                  <span className="font-semibold text-green-600">85%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stockage local</span>
                  <span className="font-semibold text-gray-900">2.1 MB</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Dernière sync</span>
                  <span className="font-semibold text-gray-900">Il y a 5 min</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Économies de données</span>
                  <span className="text-green-600 font-medium">92%</span>
                </div>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full" style={{ width: '92%' }}></div>
                </div>
              </div>
            </div>

            {/* Conseils pour le mode hors ligne */}
            <div className="bg-gradient-to-br from-blue-500 to-teal-600 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-4">Conseils hors ligne</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <i className="ri-lightbulb-line mt-0.5"></i>
                  <span>Préparez vos commandes à l'avance</span>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-lightbulb-line mt-0.5"></i>
                  <span>Synchronisez dès que possible</span>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-lightbulb-line mt-0.5"></i>
                  <span>Vérifiez vos adresses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
